// ═══════════════════════════════════════════════════════════════════════════
//  GOD CHAKRA — SMC Matrix + Ultra-Scalp fusion for the SuperChart.
//
//  All heavy signal math (FVG / Order Blocks / Liquidity Sweeps / EMA stack /
//  Fib pocket / Pivots / PCR) runs SERVER-SIDE in the SMC engine — one
//  GET /smc-matrix?symbol=X call returns a pre-computed row for EVERY
//  timeframe. This module only FUSES those rows into a single verdict:
//
//    · base row  = the chart's own TF        (execution signal)
//    · HTF rows  = two higher timeframes     (market-direction filter)
//
//  Verdict = weighted directional score. A BUY needs the base TF bullish AND
//  the higher timeframes not fighting it (and vice-versa for SELL) — the same
//  top-down flow a discretionary trader uses. Everything else is WAIT.
//
//  The latest verdict per symbol is cached here and broadcast as a
//  'god-chakra' CustomEvent so bots / Mamta AI can read price behaviour
//  without touching the chart.
// ═══════════════════════════════════════════════════════════════════════════

export interface GodStruct { type: string; mid?: number; level?: number }
export interface GodRow {
  tf: string;
  status?: string;
  price: number;
  trend: string;
  ema5?: number | null;
  ema13?: number | null;
  ema50?: number | null;
  ema200?: number | null;
  order_block?: GodStruct | null;
  fvg?: GodStruct | null;
  liq_sweep?: GodStruct | null;
  resistance?: { level: number; label?: string; dist_pct?: number } | null;
  support?: { level: number; label?: string; dist_pct?: number } | null;
  fib?: { status?: string; '0.382'?: number; '0.618'?: number; high?: number; low?: number } | null;
  pivot?: { P: number; R1: number; S1: number; R2: number; S2: number; R3?: number; S3?: number } | null;
  pcr?: number | string;
}

export interface GodTfScore {
  tf: string;
  row: GodRow;
  /** -100 (max bear) .. +100 (max bull) */
  pct: number;
  dir: 'BULL' | 'BEAR' | 'FLAT';
}

export interface GodVerdict {
  action: 'BUY' | 'SELL' | 'WAIT';
  /** 0..100 — strength of the weighted multi-TF agreement */
  confluence: number;
  /** true when base + every HTF point the same non-flat way */
  aligned: boolean;
  base: GodTfScore;
  htf: GodTfScore[];
  reasons: string[];
}

// Full TF ladder in ascending order — used to fall back to the nearest
// available higher row when the ideal HTF is missing from the response.
const TF_LADDER = ['1s', '5s', '15s', '1m', '3m', '5m', '15m', '30m', '1H', '4H', '1D', '1W'];

// Ideal higher-TF pair per chart TF. Mirrors the Rudra bot DNA:
// Vega scalps 1s-1m against 15m flow, Shool trades 5m against 15m/1H,
// Sthambh enters 15m gated by 4H + 1D.
const HTF_CHAIN: Record<string, string[]> = {
  '1s':  ['1m', '15m'],
  '5s':  ['5m', '15m'],
  '15s': ['15m', '1H'],
  '1m':  ['15m', '1H'],
  '3m':  ['15m', '1H'],
  '5m':  ['15m', '1H'],
  '15m': ['4H', '1D'],
  '30m': ['4H', '1D'],
  '1H':  ['4H', '1D'],
  '4H':  ['1D', '1W'],
  '1D':  ['1W'],
  '1W':  [],
};

const findRow = (matrix: GodRow[], tf: string): GodRow | null => {
  const tfl = tf.toLowerCase();
  return matrix.find(r => String(r.tf).toLowerCase() === tfl) || null;
};

// Nearest available row at-or-above the wanted TF (skipping TFs at or below
// the chart TF so a missing '4H' can degrade to '1D' but never to '5m').
const resolveHtfRow = (matrix: GodRow[], wantTf: string, aboveTf: string): GodRow | null => {
  const exact = findRow(matrix, wantTf);
  if (exact) return exact;
  const floor = TF_LADDER.indexOf(aboveTf);
  const from = TF_LADDER.indexOf(wantTf);
  if (from < 0) return null;
  for (let i = from + 1; i < TF_LADDER.length; i++) {
    if (i <= floor) continue;
    const r = findRow(matrix, TF_LADDER[i]);
    if (r) return r;
  }
  return null;
};

const structDir = (s?: GodStruct | null): number => {
  const t = (s?.type || '').toUpperCase();
  if (t.includes('BULL') || t.includes('LONG')) return 1;
  if (t.includes('BEAR') || t.includes('SHORT')) return -1;
  return 0;
};

/** Directional score for one backend row: -100..+100. */
export function scoreRow(row: GodRow): GodTfScore {
  let score = 0;
  const trendDir = structDir({ type: row.trend });
  score += trendDir * 2;

  // EMA stack: how many of 5/13/50/200 the price sits above.
  const emas = [row.ema5, row.ema13, row.ema50, row.ema200].filter((v): v is number => v != null && v > 0);
  if (emas.length >= 2 && row.price > 0) {
    const above = emas.filter(v => row.price >= v).length;
    const frac = above / emas.length;
    score += frac >= 0.99 ? 2 : frac >= 0.74 ? 1 : frac <= 0.01 ? -2 : frac <= 0.26 ? -1 : 0;
  }

  score += structDir(row.fvg);
  score += structDir(row.order_block);
  score += structDir(row.liq_sweep);

  // Fib golden pocket only matters in the direction of the trend.
  const fibStatus = (row.fib?.status || '').toUpperCase();
  if (trendDir !== 0 && (fibStatus.includes('GOLDEN') || fibStatus === 'IN')) score += trendDir;

  const pcr = typeof row.pcr === 'number' ? row.pcr : parseFloat(String(row.pcr));
  if (!isNaN(pcr)) score += pcr >= 1.2 ? 1 : pcr <= 0.8 ? -1 : 0;

  const pct = Math.max(-100, Math.min(100, Math.round((score / 8) * 100)));
  return { tf: row.tf, row, pct, dir: pct > 12 ? 'BULL' : pct < -12 ? 'BEAR' : 'FLAT' };
}

/** Fuse the chart-TF row with its higher-TF rows into one verdict. */
export function computeGodVerdict(matrix: GodRow[], chartTf: string): GodVerdict | null {
  if (!Array.isArray(matrix) || matrix.length === 0) return null;
  const baseRow = findRow(matrix, chartTf) || matrix[0];
  if (!baseRow || !(baseRow.price > 0)) return null;

  const base = scoreRow(baseRow);
  const htf: GodTfScore[] = [];
  const usedTfs = new Set([String(baseRow.tf).toLowerCase()]);
  for (const want of HTF_CHAIN[chartTf] ?? HTF_CHAIN[baseRow.tf] ?? []) {
    const r = resolveHtfRow(matrix, want, baseRow.tf);
    if (r && !usedTfs.has(String(r.tf).toLowerCase())) {
      usedTfs.add(String(r.tf).toLowerCase());
      htf.push(scoreRow(r));
    }
  }

  // Weighted fusion: execution TF carries half, direction filter the rest.
  const weights = htf.length === 2 ? [0.5, 0.3, 0.2] : htf.length === 1 ? [0.6, 0.4] : [1];
  const scores = [base, ...htf];
  const total = scores.reduce((acc, s, i) => acc + s.pct * weights[i], 0);
  const htfAvg = htf.length ? htf.reduce((a, s) => a + s.pct, 0) / htf.length : 0;

  const aligned = scores.every(s => s.dir === 'BULL') || scores.every(s => s.dir === 'BEAR');
  let action: GodVerdict['action'] = 'WAIT';
  if (total >= 25 && htfAvg > -10) action = 'BUY';
  else if (total <= -25 && htfAvg < 10) action = 'SELL';

  const reasons: string[] = [];
  reasons.push(`${base.tf} ${base.dir} ${base.pct > 0 ? '+' : ''}${base.pct}`);
  for (const h of htf) reasons.push(`HTF ${h.tf} ${h.dir} ${h.pct > 0 ? '+' : ''}${h.pct}`);
  if (action === 'WAIT' && Math.abs(total) >= 25) reasons.push('HTF conflict — direction filter blocked entry');
  if (aligned) reasons.push('ALL TF ALIGNED');

  return { action, confluence: Math.min(100, Math.round(Math.abs(total))), aligned, base, htf, reasons };
}

// ─── Per-symbol verdict cache + broadcast ────────────────────────────────────
// Bots / Mamta AI read the latest chart-side market read from here (or listen
// for the 'god-chakra' window event) — no chart coupling needed.
export interface GodSnapshot extends GodVerdict { symbol: string; chartTf: string; at: number }

const _latest = new Map<string, GodSnapshot>();

export function publishGodVerdict(symbol: string, chartTf: string, v: GodVerdict | null): void {
  if (!v) { _latest.delete(symbol); return; }
  const snap: GodSnapshot = { ...v, symbol, chartTf, at: Date.now() };
  _latest.set(symbol, snap);
  try { window.dispatchEvent(new CustomEvent('god-chakra', { detail: snap })); } catch { /* SSR/test safety */ }
}

export function getGodVerdict(symbol: string): GodSnapshot | null {
  return _latest.get(symbol) || null;
}

export function getAllGodVerdicts(): GodSnapshot[] {
  return Array.from(_latest.values());
}
